import { useCallback, useEffect, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, LogSentryException, TextField } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { AuthError } from "../../types";
import { FormWrapper } from "../FormWrapper/FormWrapper";
import Heading from "../Heading/Heading";
import ReCAPTCHA, { type ReCAPTCHAAPI } from "../ReCAPTCHA/ReCAPTCHA";
import Disclaimer from "../SignUp/Disclaimer/Disclaimer";
import {
  passwordSchema,
  ValidatedPasswordInput,
} from "../ValidatedPasswordInput/ValidatedPasswordInput";

import style from "./ProjectViewLinkInvitationSignUp.module.css";

interface SignUpProps {
  submit: (email: string, password: string, name: string, recaptcha?: string) => void;
  inProgress: boolean;
  signupError?: AuthError | null;
  disableCaptcha?: boolean;
  emailHint: string;
}

const errorMessage = {
  InvalidEmail: "Please enter a valid email address.",
  EmptyPassword: "Please enter your password.",
  EmptyName: "Please enter your name.",
  WeakPassword: "Please enter a strong password.",
  TooLongPassword: "Password is too long.",
  PasswordsDontMatch: "Passwords do not match.",
  TermsAndPrivacyNotAccepted: "Please agree to our Terms and Privacy Policy before proceeding.",
  EmailAlreadyUsed: "This email is already taken. Please log in.",
  CaptchaError: "Something went wrong. Please refresh the page and try again.",
  Unknown: "Something went wrong. Please try again.",
};

const schema = z
  .object({
    email: z.string(),
    name: z.string().trim().min(1, { message: errorMessage.EmptyName }),
    password: passwordSchema,
    passwordAgain: z.string(),
    areTermsAccepted: z.boolean().default(false),
  })
  .refine((data) => data.password !== "", {
    path: ["passwordAgain"],
    message: errorMessage.EmptyPassword,
  })
  .refine((data) => data.password === data.passwordAgain, {
    path: ["passwordAgain"],
    message: errorMessage.PasswordsDontMatch,
  })
  .refine((data) => data.areTermsAccepted, {
    path: ["areTermsAccepted"],
    message: errorMessage.TermsAndPrivacyNotAccepted,
  });

type SignUpFormData = z.infer<typeof schema>;

export const ProjectViewLinkInvitationSignUp = ({
  submit,
  inProgress,
  signupError,
  disableCaptcha,
  emailHint,
}: SignUpProps) => {
  const [isCaptchaEnabled, setIsCaptchaEnabled] = useState(false);
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHAAPI>(null);

  const {
    handleSubmit,
    control,
    register,
    setError,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      email: emailHint,
      password: "",
      passwordAgain: "",
      name: "",
      areTermsAccepted: false,
    },
  });

  useEffect(() => {
    if (signupError === AuthError.InvalidEmail) {
      setError("root", { message: errorMessage.InvalidEmail });
    } else if (signupError === AuthError.EmailAlreadyUsed) {
      setError("root", { message: errorMessage.EmailAlreadyUsed });
    } else if (signupError === AuthError.Unknown) {
      setError("root", { message: errorMessage.Unknown });
    } else if (signupError === AuthError.MissingCaptcha) {
      setIsCaptchaEnabled(true);
    }
  }, [signupError, setError]);

  const onSubmit = useCallback(
    async (data: SignUpFormData) => {
      const { email, password, name } = data;

      if (disableCaptcha || !isCaptchaEnabled) {
        submit(email, password, name);
        return;
      }

      if (recaptchaRef.current === null) {
        setError("root", { type: "manual", message: errorMessage.CaptchaError });
        return;
      }

      try {
        const recaptchaValue = await recaptchaRef.current.execute();
        if (recaptchaValue === "cancel") {
          return;
        }
        if (!recaptchaValue) {
          setError("root", { type: "manual", message: errorMessage.CaptchaError });
          return;
        }
        submit(email, password, name, recaptchaValue);
      } catch (error) {
        setError("root", { type: "manual", message: errorMessage.CaptchaError });
        LogSentryException({ messagePrefix: "Captcha error", error });
      }
    },
    [disableCaptcha, isCaptchaEnabled, submit, setError]
  );

  useEffect(() => {
    if (
      isCaptchaEnabled &&
      isRecaptchaLoaded &&
      recaptchaRef.current !== null &&
      signupError === AuthError.MissingCaptcha
    ) {
      handleSubmit(onSubmit)();
    }
  }, [isCaptchaEnabled, recaptchaRef, handleSubmit, isRecaptchaLoaded, signupError, onSubmit]);

  return (
    <FormWrapper>
      <div className={style.modalContainer}>
        <Heading type="h2" aria-label="title">
          Create a Shapr3D account <br /> to review and comment
        </Heading>
        <form className={style.signUpForm} onSubmit={handleSubmit(onSubmit)} noValidate>
          <TextField
            errorMessage={errors.email?.message}
            type="email"
            aria-label="email"
            {...register("email")}
            error={Boolean(errors.email)}
            disabled
          />

          <ValidatedPasswordInput
            type="password"
            placeholder={"Password"}
            autoComplete="new-password"
            error={Boolean(errors.password)}
            disabled={inProgress}
            {...register("password")}
          />

          <TextField
            errorMessage={errors.passwordAgain?.message}
            type="password"
            aria-label="repeat password"
            placeholder={"Re-enter password"}
            {...register("passwordAgain")}
            error={Boolean(errors.password) || Boolean(errors.passwordAgain)}
            disabled={inProgress}
          />

          <TextField
            errorMessage={errors.name?.message}
            aria-label="name"
            placeholder={"Your name"}
            {...register("name")}
            error={Boolean(errors.name)}
            disabled={inProgress}
            maxLength={200}
          />

          <div>
            <Controller
              name="areTermsAccepted"
              control={control}
              render={({ field }) => (
                <Disclaimer
                  className={classNames(style.disclaimer)}
                  areTermsAccepted={field.value}
                  handleToggleTermsAccepted={() => field.onChange(!field.value)}
                />
              )}
            />
            {errors.areTermsAccepted && (
              <span className={style.disclaimerErrorMessage}>
                {errors.areTermsAccepted.message}
              </span>
            )}
          </div>

          {isCaptchaEnabled && !disableCaptcha && (
            <ReCAPTCHA
              ref={recaptchaRef}
              onLoad={() => {
                setIsRecaptchaLoaded(true);
              }}
            />
          )}

          {errors.root && <span className={style.rootErrorMessage}>{errors.root.message}</span>}

          <Button id="signup-button" type="submit" aria-label="signup" disabled={inProgress}>
            {"Sign up & review design"}
          </Button>
        </form>
      </div>
    </FormWrapper>
  );
};
